import {useMemo} from "react";
import {DealType} from "../domain/DealSelection.model";
import {DataTableUI} from "../../ui/components/DataTableUI";
import {HoverPopoverUI} from "../../common/ui/HoverPopoverUI";
import InfoTwoToneIcon from "@mui/icons-material/InfoTwoTone";
import {spanishDateFormatter} from "../../ui/components/SpanishDateFormatter";


export const DealSelector = ({product, selection, onSelectionChange}) => {

    const handleDealSelection = (offer) => {
        onSelectionChange({type: DealType.OFFER, value: offer});
    };

    const offersTableData = useMemo(
        () => buildOfferTableData(product.offers, selection?.value, handleDealSelection),
        [product.offers, selection?.value]
    );

    const handlePromoSelection = (promo) => {
        onSelectionChange({type: DealType.PROMO, value: promo});
    };

    const promoTableData = useMemo(
        () => buildPromoTableData(product.promos, selection?.value, handlePromoSelection),
        [product.lot, selection?.value]
    );

    const handleLotSelection = (lot) => {
        onSelectionChange({type: DealType.LOT, value: lot});
    };

    const lotTableData = useMemo(
        () => buildLotTableData(product.lots, selection?.value, handleLotSelection),
        [product.lot, selection?.value]
    );
    return (
        <div className="fw-lighter">
            <DataTableUI title="Ofertas exclusivas" tableData={offersTableData}/>
            <DataTableUI title="Promociones con obsequios" tableData={promoTableData}/>
            <DataTableUI title="Promociones por lotes" tableData={lotTableData}/>
            {
                product.hasDealsToChoose && (
                    <div className="px-2 d-flex flex-row gap-3 justify-content-start align-items-center">
                        <input type="radio"
                               name="noDeal"
                               checked={!selection}
                               onChange={(event) => {
                                   event.target.checked && onSelectionChange(null);
                               }}/>
                        <strong>No quiero ninguna promoción</strong>
                    </div>
                )
            }
        </div>
    );
};

const buildOfferTableData = (offers = [], selected, onSelect) => {
    const hasConditions = offers.some(o => o.conditions?.trim());
    const columns = buildOfferColumns(hasConditions);

    const items = offers.map((offer, idx) =>
        ({
            key: String(idx),
            cells: buildOfferItemCells(offer, selected === offer, onSelect, hasConditions)
        })
    );

    return {columns, items};
};

const buildOfferColumns = (hasConditions) => [
    {key: 'select', header: ''},
    {key: 'minQty', header: 'Cantidad mínima'},
    {key: 'discount', header: 'Descuento'},
    {key: 'price', header: 'Precio neto'},
    ...(hasConditions ? [{key: 'conditions', header: 'Condiciones'}] : []),
];

const buildOfferItemCells = (offer, isSelected, onSelect, includeConditions) => {
    const cells = [
        <input
            type="radio"
            name="offer"
            checked={isSelected}
            onChange={() => onSelect(offer)}
        />,
        `${offer.minQuantity} Uds`,
        `${offer.price.discount.toFixed(2)}%`,
        `${offer.price.bestPrice.toFixed(2)}€`,
    ];

    if (includeConditions) {
        const conditionsCell = offer.conditions ? (
            <HoverPopoverUI message={offer.conditions}>
                <InfoTwoToneIcon style={{cursor: 'pointer', width: '2rem'}}/>
            </HoverPopoverUI>
        ) : null;

        cells.push(conditionsCell);
    }

    return cells
};

const buildPromoTableData = (promos = [], selected, onSelect) => {
    const columns = buildPromoColumns();

    const items = promos.map((promo, idx) =>
        ({
            key: String(idx),
            cells: buildPromoItemCells(promo, selected === promo, onSelect)
        })
    );

    return {columns, items};
};

const buildPromoColumns = () => [
    {key: 'select', header: ''},
    {key: 'desc', header: 'Descripción'},
    {key: 'minQuantity', header: 'Cant. Min.'},
    {key: 'priceGift', header: 'Precio obsequio'},
    {key: 'pricePack', header: 'Precio pack'},
];

const buildPromoItemCells = (promos, isSelected, onSelect) => {
    return [
        <input
            type="radio"
            name="offer"
            checked={isSelected}
            onChange={() => onSelect(promos)}
        />,
        `${promos.description}`,
        `${promos.minPurchaseForPromo} Uds`,
        `${promos.price.bestPrice.toFixed(2)}€`,
        `${promos.packPrice.toFixed(2)}€`,
    ];
};

const buildLotTableData = (lots = [], selected, onSelect) => {
    const columns = buildLotColumns();

    const items = lots.map((lot, idx) =>
        ({
            key: String(idx),
            cells: buildLotItemCells(lot, selected === lot, onSelect)
        })
    );

    return {columns, items};
};

const buildLotColumns = () => [
    {key: 'select', header: ''},
    {key: 'price', header: 'Precio'},
    {key: 'discount', header: 'Descuento'},
    {key: 'netPrice', header: 'Precio Neto'},
    {key: 'stock', header: 'Stock lote'},
    {key: 'expirationDate', header: 'Fecha de caducidad'},
];

const buildLotItemCells = (lot, isSelected, onSelect) => {
    return [
        <input
            type="radio"
            name="offer"
            checked={isSelected}
            onChange={() => onSelect(lot)}
        />,
        `${lot.price.value.toFixed(2)}€`,
        `${lot.price.discountLine}`,
        `${lot.price.bestPrice.toFixed(2)}€`,
        `${lot.stock}`,
        `${spanishDateFormatter(lot.expirationDate)}`,
    ];
};

